import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context';
import { useTheme } from '@/hooks';
import { NotificationDropdown } from './NotificationDropdown';
import { HelpCenter } from '@/components/help';
import { DateTimeClock } from '@/components/common';
import styles from './Header.module.css';

interface HeaderProps {
  title?: string;
  onMenuToggle?: () => void;
  isPlatformUser?: boolean;
}

export function Header({ title = 'OHCS', onMenuToggle, isPlatformUser = false }: HeaderProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        {onMenuToggle && (
          <button
            className={styles.menuButton}
            onClick={onMenuToggle}
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}
        <Link to="/dashboard" className={styles.logo} data-tour-target="header-logo">
          <div className={styles.logoIcon}>
            <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
              {/* Folder base */}
              <path
                d="M4 14C4 11.7909 5.79086 10 8 10H18L22 14H40C42.2091 14 44 15.7909 44 18V38C44 40.2091 42.2091 42 40 42H8C5.79086 42 4 40.2091 4 38V14Z"
                className={styles.logoFolderBase}
              />
              {/* Document pages */}
              <rect x="12" y="20" width="24" height="4" rx="1" className={styles.logoDocLine} />
              <rect x="12" y="26" width="18" height="3" rx="1" className={styles.logoDocLine} />
              <rect x="12" y="31" width="20" height="3" rx="1" className={styles.logoDocLine} />
              {/* Ghana star accent */}
              <circle cx="38" cy="8" r="6" className={styles.logoStarCircle} />
              <text x="38" y="11" textAnchor="middle" className={styles.logoStarText}>★</text>
            </svg>
          </div>
          <div className={styles.logoText}>
            <span className={styles.logoTitle}>{title}</span>
            <span className={styles.logoSubtitle}>Records Management System</span>
          </div>
        </Link>
      </div>

      <div className={styles.right}>
        {/* Clock Widget */}
        <div className={styles.clockWrapper}>
          <DateTimeClock variant={isPlatformUser ? 'platform' : 'institutional'} compact />
        </div>

        {/* Divider */}
        <div className={styles.divider} aria-hidden="true" />

        {/* Theme Toggle */}
        <button onClick={toggleTheme} className={styles.themeToggle} aria-label="Toggle theme">
          {theme === 'dark' ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="5" />
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>

        {/* Help Button */}
        <button
          className={styles.themeToggle}
          onClick={() => setIsHelpOpen(true)}
          aria-label="Help"
          data-tour-target="header-help"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </button>

        {user && (
          <>
            <div data-tour-target="header-notifications">
              <NotificationDropdown />
            </div>
            <div className={styles.userMenu} data-tour-target="header-user">
              <Link to="/profile" className={styles.userInfo}>
                <span className={styles.userName}>
                  {user.firstName} {user.lastName}
                </span>
                <span className={styles.userEmail}>{user.email}</span>
              </Link>
              <button className={styles.logoutButton} onClick={logout} aria-label="Logout">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M13.333 14.167L17.5 10m0 0l-4.167-4.167M17.5 10H7.5m0-7.5h-1.667a2.5 2.5 0 00-2.5 2.5v10a2.5 2.5 0 002.5 2.5H7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Help Center Modal */}
      <HelpCenter
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        isPlatformUser={isPlatformUser}
      />
    </header>
  );
}
